import { createContext, useContext, useSyncExternalStore } from 'react';
import { getNotifications, subscribeNotifications } from './notificationStore.js';
import type { NotificationData, NotificationStore } from './notificationStore.js';
import type { ReactNode } from 'react';

// NOTE! DO NOT use the `NotificationContext` outside of this file, except for tests. It is only exported for tests.
export const NotificationContext = createContext<NotificationData>({ items: [] });

const emptyNotifications: NotificationData = { items: [] };

interface NotificationProviderProps {
  notificationStore?: NotificationStore;
  children?: ReactNode;
}

/**
 * - Client-side: omit `notificationStore` prop — uses the default module-level singleton (safe: one user per tab).
 * - SSR: pass a fresh `createNotificationStore()` instance per request to avoid cross-request leakage.
 */
export const NotificationProvider = ({ notificationStore, children }: NotificationProviderProps) => {
  const subscribe = notificationStore ? notificationStore.subscribe : subscribeNotifications;
  const getSnapshot = notificationStore ? notificationStore.getNotifications : getNotifications;
  const data = useSyncExternalStore(subscribe, getSnapshot, () => emptyNotifications);
  return <NotificationContext.Provider value={data}>{children}</NotificationContext.Provider>;
};

export const useNotifications = (): NotificationData => useContext(NotificationContext);
